import * as React from "react";
import * as htmlToImage from "html-to-image";
import { useTranslation } from "react-i18next";
import { delay } from "../helpers/helpers";
import { Document, Image, Page, Text, View } from "@react-pdf/renderer";
import {
  CGI_ERROR,
  CGI_RED,
  CGI_SUCCESS,
  discreteDescriptions,
  returnColor,
} from "../helpers/enums";

export default function CreatePdfDocument({
  moduleData,
  selectedCustomer,
  pdfReady,
  setPdfReady,
  selectedAssessment,
  selectedEvaluation,
  introText,
  moduleHeaderText = "",
  questionsHeaderText = "",
}) {
  const { t, i18n } = useTranslation();
  const [imgUrl, setImgUrl] = React.useState("");
  const [radarChartImgUrl, setRadarChartImgUrl] = React.useState("");

  function getDateLocale() {
    const translatedLocale = t("timeSettings");
    if (translatedLocale && translatedLocale !== "timeSettings") {
      return translatedLocale;
    }

    if (i18n.resolvedLanguage?.toLowerCase().startsWith("de")) {
      return "de-DE";
    }

    if (i18n.resolvedLanguage?.toLowerCase().startsWith("fr")) {
      return "fr-FR";
    }

    return "en-US";
  }

  React.useEffect(() => {
    let cancelled = false;

    const preparePdfImages = async () => {
      await delay(5000);

      const iconId = document.getElementById("menuBarHCLogo");
      if (iconId) {
        const newImgUrl = await htmlToImage.toPng(iconId);
        if (!cancelled) setImgUrl(newImgUrl);
      }

      const radarChartId = document.getElementById("radarChartId");
      if (radarChartId) {
        const newRadarChartId = await htmlToImage.toPng(radarChartId);
        if (!cancelled) setRadarChartImgUrl(newRadarChartId);
      }

      await delay(1000);
      if (!cancelled) setPdfReady(true);
    };

    preparePdfImages().catch((error) => {
      console.error(error);
      if (!cancelled) setPdfReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const textStyle = {
    marginTop: 6,
    fontSize: 8,
    textAlign: "justify",
    fontFamily: "Montserrat",
    marginLeft: 10,
    paddingRight: 20,
  };

  const listStyle = {
    marginTop: 3,
    fontSize: 6,
    textAlign: "justify",
    fontFamily: "Montserrat",
  };

  const headerStyle = {
    marginTop: 6,
    fontSize: 10,
    textAlign: "justify",
    fontFamily: "Montserrat",
    textDecoration: "underline",
  };

  const date = new Date();

  return (
    <Document>
      {pdfReady && (
        <Page
          size="A4"
          style={{
            flexDirection: "column",
            backgroundColor: "#fff",
            paddingTop: 35,
            paddingBottom: 55,
          }}
        >
          {imgUrl && (
            <View
              style={{
                width: "100%",
                backgroundColor: CGI_RED,
                display: "flex",
                alignItems: "flex-end",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
              }}
            >
              <Image
                src={imgUrl}
                style={{
                  height: 50,
                  padding: 5,
                }}
              />
            </View>
          )}

          <Text
            style={{
              fontSize: 12,
              textAlign: "justify",
              fontFamily: "Montserrat",
              marginLeft: 20,
              marginTop: 25,
              paddingBottom: 5,
              borderBottom: 1,
              marginRight: 25,
            }}
          >
            {`${t("applicationName")}: `}
            {t("evaluationOfTheAssessment")}
          </Text>

          <Text
            style={{
              fontSize: 11,
              textAlign: "justify",
              fontFamily: "Montserrat",
              marginLeft: 20,
              marginTop: 5,
              marginRight: 100,
            }}
          >
            {t("customer")} {selectedCustomer.name} {"  |  "}
            {`${selectedAssessment}\n`}
            {`${t("evaluationFrom")}: `}
            {new Date(selectedEvaluation.createdAt).toLocaleString(
              getDateLocale(),
              { timeZone: "Europe/Berlin" },
            )}
          </Text>

          <Text
            style={{
              fontSize: 8,
              textAlign: "justify",
              fontFamily: "Montserrat",
              marginLeft: 25,
              marginTop: 5,
            }}
          >
            {`${t("createdAt")} `}
            {date.toLocaleString(getDateLocale(), {
              timeZone: "Europe/Berlin",
            })}
          </Text>

          <View
            style={{
              width: "auto",
              display: "flex",
              flexDirection: "column",
              marginLeft: 50,
              marginTop: 5,
            }}
          >
            <Text style={headerStyle}>{`${t("dataByModule")}:`}</Text>
            <Text style={{ ...textStyle, marginBottom: 15, marginRight: 30 }}>
              {moduleHeaderText}
            </Text>

            {moduleData.map((categoryData, i) => (
              <Text style={textStyle} key={i}>
                • {categoryData.name}: {categoryData.rating} /{" "}
                {categoryData.maxRating} {t("points")} -{" "}
                {categoryData.maxRating > 0
                  ? Math.round(
                      (categoryData.rating / categoryData.maxRating) * 100,
                    )
                  : 0}
                % {t("fulfilleds")}
              </Text>
            ))}
          </View>

          {radarChartImgUrl && (
            <Image
              src={radarChartImgUrl}
              style={{
                marginTop: 10,
                width: "100%",
                height: "auto",
              }}
            />
          )}

          <View
            break={true}
            style={{
              width: "auto",
              display: "flex",
              flexDirection: "column",
              marginTop: 10,
            }}
          >
            <Text
              style={{
                marginLeft: 45,
                fontSize: 12.5,
                textAlign: "justify",
                fontFamily: "Montserrat",
                textDecoration: "underline",
                fontWeight: 500,
              }}
            >
              {t("summaryOfResults")}
            </Text>
            <Text
              style={{
                ...textStyle,
                marginBottom: 15,
                marginHorizontal: 30,
                paddingLeft: 20,
                whiteSpace: "pre-line",
              }}
            >
              {introText}
            </Text>
          </View>

          <View
            break={true}
            style={{
              width: "auto",
              display: "flex",
              flexDirection: "column",
              marginLeft: 40,
              marginTop: 20,
            }}
          >
            <Text
              style={{
                marginHorizontal: 6,
                fontSize: 12.5,
                textAlign: "justify",
                fontFamily: "Montserrat",
                textDecoration: "underline",
                fontWeight: 500,
              }}
            >
              {t("resultsOfAllQuestions")}
            </Text>
            <Text
              style={{
                ...textStyle,
                borderBottom: 0.5,
                paddingBottom: 15,
                marginRight: 50,
                whiteSpace: "pre-line",
              }}
            >
              {questionsHeaderText}
            </Text>

            {moduleData
              .filter((x) => !x.hideModule)
              .map((data, i) => (
                <View key={i} style={{ marginRight: 20 }}>
                  <View
                    style={{
                      width: "auto",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      marginTop: 20,
                    }}
                  >
                    <Text
                      style={{
                        ...headerStyle,
                        fontWeight: 500,
                        marginLeft: 7.5,
                      }}
                    >
                      {i + 1}. {data.name}
                    </Text>
                    <Text
                      style={{
                        marginTop: 6,
                        fontSize: 11,
                        textAlign: "justify",
                        fontFamily: "Montserrat",
                        marginRight: 40,
                      }}
                    >
                      {t("maturityLevel")}:{" "}
                      {data.maxRating > 0
                        ? Math.round((data.rating / data.maxRating) * 100)
                        : 0}
                      %
                    </Text>
                  </View>

                  <Text
                    style={{
                      ...textStyle,
                      borderBottom: 0.5,
                      paddingBottom: 15,
                      marginRight: 30,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {data.generatedText}
                  </Text>

                  {data.questionRating
                    ?.filter((q) => q.hasAnswer)
                    .map((question, qIndex) => {
                      const ratingPercent =
                        question.maxRating > 0
                          ? Math.round(
                              (question.rating / question.maxRating) * 100,
                            )
                          : 0;

                      return (
                        <View
                          key={qIndex}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            borderBottom: 0.25,
                            paddingBottom: 10,
                            marginRight: 30,
                            marginTop: 10,
                          }}
                        >
                          <View
                            style={{ display: "flex", flexDirection: "row" }}
                          >
                            <Text
                              style={{
                                ...textStyle,
                                textDecoration: "underline",
                              }}
                            >
                              {t("question")} {i + 1}.{qIndex + 1}
                            </Text>
                            <Text
                              style={{
                                ...textStyle,
                                marginLeft: 4,
                                color:
                                  ratingPercent >= 50 ? CGI_SUCCESS : CGI_ERROR,
                                fontWeight: 500,
                              }}
                            >
                              {ratingPercent >= 50
                                ? t("requirementsFulfilled")
                                : t("actionRequired")}
                            </Text>
                            <Text style={textStyle}>
                              {t("maturityLevel")}: {ratingPercent}%
                            </Text>
                          </View>

                          <Text style={textStyle}>
                            {t("module:")} {question.questionData?.categoryName}
                          </Text>
                          <Text style={textStyle}>
                            {t("question:")} {question.questionData?.text}
                          </Text>
                          <Text style={textStyle}>
                            {`${t("contactPerson")}: `}
                            {question.contactPerson || "n/a"}
                          </Text>
                          <Text style={textStyle}>
                            {`${t("comments")}: `}
                            {question.answerText || "n/a"}
                          </Text>

                          <Text style={textStyle}>
                            {t("requirementsInDetail")}
                          </Text>

                          {question.requirements?.map((req) =>
                            req.requirementAnswers.map((element, rIndex) => {
                              const isGradation = req.gradations;
                              const maxLevel = req.recommendations?.length
                                ? req.recommendations.length - 1
                                : 0;
                              const selected = element.selectedLevel ?? 0;

                              let displayText = "";
                              let showRecommendation = false;

                              if (element.irrelevant) {
                                displayText = t("notRelevant");
                              } else if (isGradation) {
                                const discreteIndex =
                                  maxLevel > 0
                                    ? Math.round((selected / maxLevel) * 3)
                                    : 0;
                                displayText = t(
                                  discreteDescriptions[discreteIndex],
                                );
                                showRecommendation = selected < maxLevel;
                              } else {
                                if (element.fulfilled === true) {
                                  displayText = t("fulfilled");
                                  showRecommendation = false;
                                } else if (element.fulfilled === false) {
                                  displayText = t("notFulfilled");
                                  showRecommendation = true;
                                }
                              }

                              return (
                                <View
                                  key={rIndex}
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    marginTop: 2,
                                  }}
                                >
                                  <Text
                                    style={{
                                      ...listStyle,
                                      marginLeft: 1,
                                      fontWeight: 500,
                                      minWidth: 50,
                                      textAlign: "right",
                                      maxWidth: 50,
                                      marginRight: 5,
                                      color: returnColor(
                                        element.irrelevant,
                                        selected,
                                        maxLevel,
                                        element.fulfilled,
                                      ),
                                    }}
                                  >
                                    {"• "} {displayText}
                                    {displayText ? ":" : ""}
                                  </Text>
                                  <View
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      marginRight: 70,
                                    }}
                                  >
                                    <Text style={listStyle}>
                                      <Text style={listStyle}>
                                        <Text style={listStyle}>
                                          {`${req.text} (${t("weights", {
                                            weight: req.weight ?? 0,
                                          })})`}
                                        </Text>
                                      </Text>
                                    </Text>
                                    {showRecommendation &&
                                      req.recommendations?.length > 0 && (
                                        <Text style={listStyle}>
                                          {"→ " + t("recommendation") + ": "}
                                          {req.recommendations[selected]
                                            ?.recommendationText ||
                                            req.recommendations[0]
                                              ?.recommendationText ||
                                            t("noRecommendation")}
                                        </Text>
                                      )}
                                  </View>
                                </View>
                              );
                            }),
                          )}
                        </View>
                      );
                    })}
                </View>
              ))}
          </View>

          <Text
            style={{
              position: "absolute",
              fontFamily: "Montserrat",
              fontSize: 12,
              bottom: 10,
              left: 0,
              right: 0,
              textAlign: "center",
              color: "grey",
            }}
            render={({ pageNumber, totalPages }) =>
              `Seite ${pageNumber} / ${totalPages}`
            }
            fixed
          />

          <Text
            style={{
              position: "absolute",
              fontFamily: "Montserrat",
              fontSize: 6,
              bottom: 5,
              left: 0,
              right: 5,
              textAlign: "right",
              color: "grey",
            }}
            fixed
          >
            {t("assessmentEvaluationOfTheCGICompass")}
          </Text>
        </Page>
      )}
    </Document>
  );
}
